import { useEffect, useState } from "react";
import Card from "components/card";
import StatusBadge from "components/admin/StatusBadge";
import { apiGet } from "lib/apiClient";
import { formatKes } from "lib/format";

const HUB_TONE = { DRAFT: "gray", ACTIVE: "green", PAUSED: "amber", ARCHIVED: "gray" };

/**
 * Hubs, from a partner's own seat.
 *
 * Read-only — a hub is platform-owned structure curated from the admin workspace, not
 * something a partner edits — but the numbers on each card are the partner's OWN slice
 * of that hub: their own clusters and series inside it, their own 24h volume. A hub
 * other tenants also place markets into never leaks another tenant's figures here; the
 * API enforces that scope server-side (`/portal/hubs`), this page just renders it.
 */
export default function HubsPage() {
  const [hubs, setHubs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiGet("/portal/hubs")
      .then((result) => setHubs(result.hubs || []))
      .catch((err) => setError(err.message || "Failed to load hubs."));
  }, []);

  return (
    <div>
      <div className="mb-5">
        <p className="text-2xl font-semibold text-navy-700 dark:text-white">Hubs</p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          The sections of the platform your markets sit under, and your own activity inside each one.
        </p>
      </div>

      {error && <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">{error}</div>}

      {!hubs ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : hubs.length === 0 ? (
        <Card extra="p-8 text-center text-sm text-gray-600">No hubs are visible yet.</Card>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {hubs.map((hub) => (
            <Card key={hub.id} extra="overflow-hidden">
              {hub.imageUrl ? (
                <img src={hub.imageUrl} alt="" className="h-28 w-full object-cover" />
              ) : (
                <div
                  className="flex h-28 w-full items-center justify-center text-4xl"
                  style={{ background: hub.color ? `${hub.color}22` : undefined }}
                >
                  {hub.icon || hub.name.slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-lg font-semibold text-navy-700 dark:text-white">{hub.name}</p>
                    <p className="text-xs text-gray-500">{hub.shortDescription}</p>
                  </div>
                  <StatusBadge label={hub.status} tone={HUB_TONE[hub.status] || "gray"} />
                </div>

                <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-xs text-muted-foreground">Your series</dt>
                    <dd className="font-semibold text-navy-700 dark:text-white">
                      {hub.stats.activeSeriesCount} / {hub.stats.seriesCount}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Open markets</dt>
                    <dd className="font-semibold text-navy-700 dark:text-white">
                      {hub.stats.openInstanceCount + hub.stats.legacyOpenMarketCount}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">24h volume</dt>
                    <dd className="font-semibold text-navy-700 dark:text-white">{formatKes(hub.stats.volume24h)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">24h trades</dt>
                    <dd className="font-semibold text-navy-700 dark:text-white">{hub.stats.trades24h}</dd>
                  </div>
                </dl>

                {hub.stats.pendingApprovalCount > 0 || hub.stats.awaitingResolutionCount > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    {hub.stats.pendingApprovalCount > 0 ? (
                      <span className="rounded-full bg-amber-100 px-2 py-1 font-semibold text-amber-700 dark:bg-amber-400/20 dark:text-amber-300">
                        {hub.stats.pendingApprovalCount} awaiting approval
                      </span>
                    ) : null}
                    {hub.stats.awaitingResolutionCount > 0 ? (
                      <span className="rounded-full bg-blue-100 px-2 py-1 font-semibold text-blue-700 dark:bg-blue-400/20 dark:text-blue-300">
                        {hub.stats.awaitingResolutionCount} awaiting result
                      </span>
                    ) : null}
                  </div>
                ) : null}

                {hub.clusters.length > 0 ? (
                  <div className="mt-3 border-t border-gray-100 pt-3 dark:border-white/10">
                    <p className="mb-1 text-xs font-medium text-muted-foreground">Clusters</p>
                    <div className="flex flex-wrap gap-1">
                      {hub.clusters.map((cluster) => (
                        <span key={cluster.id} className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-navy-700 dark:bg-white/10 dark:text-white">
                          {cluster.label}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
